import React, { useContext, useEffect, useRef } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { AuthContext } from '../AuthContext';
import { t } from '../i18n';

const Navigation = () => {
  const { user, logout, pendingFriendRequests, hostingSoonCount, attendingSoonCount } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const tabNavRef = useRef(null);
  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // Restart animations on change so flashing stays in sync across tabs.
  useEffect(() => {
    const el = tabNavRef.current;
    if (!el) return;

    // Determine whether friends/calendar would be flashing for the current route
    const path = location.pathname || '';
    const calendarActive = path.startsWith('/calendar') || path.startsWith('/event');
    const friendsActive = path.startsWith('/friends') || path === '/add-friend' || path.startsWith('/invite') || path.startsWith('/users');
    const friendsFlash = !friendsActive && pendingFriendRequests > 0;
    const calendarFlash = !calendarActive && (hostingSoonCount > 0 || attendingSoonCount > 0);

    // If neither is flashing, no need to restart
    if (!friendsFlash && !calendarFlash) return;

    // Find all flashing tab elements and restart their CSS animations simultaneously
    const animated = el.querySelectorAll('.flash-request, .flash-calendar-host, .flash-calendar-attend');
    if (!animated || animated.length === 0) return;

    animated.forEach(item => { item.style.animation = 'none'; });
    // Force reflow
    // eslint-disable-next-line no-unused-expressions
    el.offsetWidth;
    animated.forEach(item => { item.style.animation = ''; });
  }, [pendingFriendRequests, hostingSoonCount, attendingSoonCount, location.pathname]);

  if (!user) {
    return (
      <nav className="nav">
        <div className="tab-nav">
          <NavLink to="/login" className={({ isActive }) => isActive ? 'tab active' : 'tab'}>{t('login')}</NavLink>
          <NavLink to="/register" className={({ isActive }) => isActive ? 'tab active' : 'tab'}>{t('register')}</NavLink>
        </div>
      </nav>
    );
  }

  const bothFlashing = (pendingFriendRequests > 0) && (hostingSoonCount > 0 || attendingSoonCount > 0);

  return (
    <nav className="nav">
      <div ref={tabNavRef} className={`tab-nav${bothFlashing ? ' flash-sync' : ''}`}>
        <NavLink to="/" end className={({ isActive }) => isActive ? 'tab active' : 'tab'}>{t('home')}</NavLink>
        <NavLink
          to="/calendar"
          className={() => {
            const path = location.pathname || '';
            const calendarActive = path.startsWith('/calendar') || path.startsWith('/event');
            let cls = calendarActive ? 'tab active' : 'tab';
            // if not on calendar page, show hosting (gold) priority, else attending (green)
            if (!calendarActive) {
              if (hostingSoonCount > 0) cls += ' flash-calendar-host';
              else if (attendingSoonCount > 0) cls += ' flash-calendar-attend';
            }
            return cls;
          }}
          onClick={(e) => {
            const path = location.pathname || '';
            const calendarActive = path.startsWith('/calendar') || path.startsWith('/event');
            if (calendarActive) {
              // emit an application-level event to ask the calendar to refresh
              try { window.dispatchEvent(new Event('calendar:refresh')); } catch (err) {}
            }
          }}
        >
          {t('calendar')}
        </NavLink>
        <NavLink
          to="/friends"
          className={() => {
            // Keep Friends tab visually active when on related friend pages (add-friend, invite, public profiles)
            const path = location.pathname || '';
            const friendsActive = path.startsWith('/friends') || path === '/add-friend' || path.startsWith('/invite') || path.startsWith('/users');
            let cls = friendsActive ? 'tab active' : 'tab';
            // flash when there are pending incoming friend requests and the user is not on friends page
            if (!friendsActive && pendingFriendRequests > 0) cls += ' flash-request';
            return cls;
          }}
        >
          {t('friends')}
        </NavLink>
        <NavLink to="/profile" className={({ isActive }) => isActive ? 'tab active' : 'tab'}>{t('profile')}</NavLink>
      </div>
      <button onClick={handleLogout} className="btn btn-secondary logout-btn">
        {t('logout')}
      </button>
    </nav>
  );
};

export default Navigation;